import React from 'react';
import { Flex, Table, Tag, Text, ButtonLink, SrOnly } from '@applyboard/crystal-ui';
import { useErrorHandler } from '~/hooks/useErrorHandler';
import { Application } from '~/pages/ApplicationDetails/data/useApplicationsByPassport';
import { useUniqueApplicationsListContext } from '../../../context/UniqueApplicationsListProvider';
import { Flag } from '../../../../../components/Flag';
import { formatDate } from '~/utils';
import { ChevronRightOutlineIcon } from '../../../../../icons';

export function UniqueApplicationsListTable() {
  const { apiError } = useErrorHandler();
  const { applications, isFetchingApplications, isApplicationsError } = useUniqueApplicationsListContext();

  React.useEffect(() => {
    if (isApplicationsError) {
      apiError();
    }
  }, [isApplicationsError, apiError]);

  return (
    <Flex direction="column">
      <div role="alert" aria-live="assertive">
        {isFetchingApplications && <SrOnly>Loading unique applications table</SrOnly>}
      </div>
      {!isFetchingApplications && applications.length > 0 && (
        <Flex direction="column" gap={6}>
          <Text variant="titleS">Other applications {`(${applications.length})`}</Text>
          <Table
            columns={[
              {
                accessorKey: 'humanReadableId',
                header: 'App. ID',
              },
              {
                accessorKey: 'applicantName',
                header: 'Applicant Name',
                cell: (application: Application) => {
                  const applicant = application.applicant;
                  const middleName = applicant.middleName ? ` ${applicant.middleName}` : '';
                  const familyName = applicant.familyName ? ` ${applicant.familyName}` : '';
                  return <Table.TextCell>{`${applicant.givenName}${middleName}${familyName}`}</Table.TextCell>;
                },
              },
              {
                accessorKey: 'studentId',
                header: 'Student ID',
                cell: (application: Application) => {
                  return (
                    <Table.Cell>
                      <Text as="div" contrast="high" id="" intent="secondary" variant="bodyM" overflow="ellipsis">
                        {application.studentPersonaInformation?.schoolInformation?.schoolAssignedStudentId ?? ''}
                      </Text>
                    </Table.Cell>
                  );
                },
              },
              {
                accessorKey: 'intakeTerm',
                header: 'Program Intake',
                cell: (application: Application) => {
                  return (
                    <Flex direction="column" gap={1} p={2} align="start">
                      <Text as="div" contrast="high" id="" intent="secondary" variant="bodyM" overflow="ellipsis">
                        {application.programIntake.program.name}
                      </Text>
                      <Tag intent="secondary" size="sm">
                        {application.programIntake.intakeTerm.name}
                      </Tag>
                    </Flex>
                  );
                },
              },
              {
                accessorKey: 'agencyName',
                header: 'Agency Name',
                cell: (application: Application) => {
                  return (
                    <Table.Cell>
                      <Text as="div" contrast="high" id="" intent="secondary" variant="bodyM" overflow="ellipsis">
                        {application.agency?.agencyName ?? ''}
                      </Text>
                    </Table.Cell>
                  );
                },
              },
              {
                accessorKey: 'nationality',
                header: 'Nationality',
                cell: (application: Application) => {
                  return (
                    <Table.Cell>
                      <Flex align="center" id={`${application.applicant.nationality.alpha2Code}-country-code`}>
                        <Flag nationalityCode={application.applicant.nationality.alpha2Code} />
                        <Table.TextCell>{application.applicant.nationality.alpha3Code}</Table.TextCell>
                      </Flex>
                    </Table.Cell>
                  );
                },
              },
              {
                accessorKey: 'submittedAt',
                header: 'Date Submitted',
                cell: (application: Application) => {
                  return (
                    <Table.TextCell>{formatDate(new Date(application.submittedAt), { month: 'short' })}</Table.TextCell>
                  );
                },
              },
              {
                accessorKey: 'status',
                header: 'Status',
                cell: (application: Application) => {
                  return (
                    <>
                      {application.hasOpenDocumentRequests &&
                        !application.status.code.startsWith('DOCUMENTS_REQUESTED') && (
                          <div aria-label="Application has open document requests">
                            <Tag icon="fill" intent="warning" size="sm">
                              Document Requested
                            </Tag>
                          </div>
                        )}
                      <div aria-label={`Application Status Is ${application.status.label}`}>
                        <Tag icon="fill" intent={application.status.intent} size="sm">
                          {application.status.label}
                        </Tag>
                      </div>
                    </>
                  );
                },
              },
              {
                accessorKey: 'link',
                header: () => <SrOnly>Application Link</SrOnly>,
                cell: (application: Application) => {
                  return (
                    <ButtonLink
                      aria-label={`Application ${application.humanReadableId} Link`}
                      emphasis="transparent"
                      href={`/applications/${application.id}`}
                      intent="secondary"
                      leadIcon={() => {
                        return <ChevronRightOutlineIcon size="md" />;
                      }}
                      onClick={() => {
                        window.location.href = `/applications/${application.id}`;
                      }}
                      size="sm"
                      width="hug"
                    />
                  );
                },
              },
            ]}
            data={applications}
            loading={isFetchingApplications}
            overflow="scroll"
          />
        </Flex>
      )}
    </Flex>
  );
}
